import React, { useState, useEffect } from 'react';
import './CoinShop.css';
import CoinPackageCard from './CoinPackageCard';
import { showRewardedAd, checkAvailability, platform, isApp } from '../../ads/adService';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { purchasePackage, initPurchases } from '../../ads/purchaseService';

const COIN_PACKAGES = [
  {
    id: 'basic',
    name: 'Básico',
    coins: 600,
    price: 0.99,
    discount: 0,
    popular: false,
    icon: '🥉'
  },
  {
    id: 'pro',
    name: 'Pro',
    coins: 2000,
    price: 1.99,
    discount: 40,
    popular: true,
    icon: '🥇'
  },
  {
    id: 'elite',
    name: 'Elite',
    coins: 5000,
    price: 2.99,
    discount: 60,
    popular: false,
    icon: '💎'
  }
];

function CoinShop({ userCoins = 0, onClose, onPurchaseSuccess }) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [adLoading, setAdLoading] = useState(false);
  const [adStatus, setAdStatus] = useState(null); // { available, videosWatchedToday, maxVideos, cooldownRemaining }

  useEffect(() => {
    loadAdStatus();
    if (isApp) initPurchases();
  }, []);

  const loadAdStatus = async () => {
    try {
      const status = await checkAvailability();
      setAdStatus(status);
    } catch (e) {
      console.error('Error checking ad availability:', e);
    }
  };

  const handleWatchAd = async () => {
    if (adLoading) return;
    setAdLoading(true);
    try {
      const result = await showRewardedAd();
      if (result.success && onPurchaseSuccess) {
        onPurchaseSuccess(result.coinsEarned);
      }
      await loadAdStatus();
    } catch (err) {
      alert(err.message || 'Error al mostrar video');
    } finally {
      setAdLoading(false);
    }
  };

  const handlePurchase = async (packageData) => {
    if (isApp) {
      // Google Play Billing
      try {
        const result = await purchasePackage(packageData.id);
        if (result.success && onPurchaseSuccess) {
          alert(`¡${result.coinsAdded} 💰 monedas acreditadas!`);
          onPurchaseSuccess(result.coinsAdded);
        }
      } catch (err) {
        if (err.message !== 'Compra cancelada') {
          alert('Error: ' + err.message);
        }
      }
    } else {
      // PayPal (web)
      setSelectedPackage(packageData);
      setShowPayPal(true);
    }
  };

  return (
    <div className="coin-shop-overlay">
      <div className="coin-shop-container">
        <button className="close-button" onClick={onClose}>✕</button>
        
        <div className="coin-shop-header">
          <h1>💰 Tienda de Monedas 💰</h1>
          <div className="current-balance">
            <span>Tu balance:</span>
            <span className="balance-amount">{userCoins} 💰</span>
          </div>
        </div>

        <div className="video-reward-section">
          <h2>{platform === 'app' ? '📺 Ver Anuncio' : '📺 Ver Video'} y Ganar Monedas</h2>
          <div className="video-reward-box">
            <div className="reward-amount">+30 💰 por video</div>
            {adStatus && (
              <div className="videos-count">
                Hoy: {adStatus.videosWatchedToday}/{adStatus.maxVideos} videos
              </div>
            )}
            <button
              className={`video-reward-button ${adStatus?.available ? 'available' : 'disabled'}`}
              onClick={handleWatchAd}
              disabled={!adStatus?.available || adLoading}
            >
              {adLoading
                ? '⏳ Cargando...'
                : adStatus?.cooldownRemaining > 0
                  ? `⏱️ Espera ${adStatus.cooldownRemaining}s`
                  : adStatus?.videosWatchedToday >= adStatus?.maxVideos
                    ? '🚫 Vuelve mañana'
                    : '▶️ VER VIDEO Y GANAR 30 💰'}
            </button>
          </div>
        </div>

        <div className="packages-section">
          <h2>💳 Comprar Monedas</h2>
          <div className="packages-grid">
            {COIN_PACKAGES.map(pkg => (
              <CoinPackageCard
                key={pkg.id}
                package={pkg}
                onPurchase={handlePurchase}
              />
            ))}
          </div>
        </div>

        <div className="shop-footer">
          <p>{isApp ? '🔒 Pago seguro con Google Play' : '💳 Pago seguro con PayPal'}</p>
          <p>🌍 Paga en tu moneda local</p>
          <p>⚡ Monedas acreditadas al instante</p>
        </div>
      </div>

      {!isApp && showPayPal && selectedPackage && (
        <div className="paypal-modal">
          <div className="paypal-content">
            <button className="close-button" onClick={() => {
              setShowPayPal(false);
              setSelectedPackage(null);
            }}>✕</button>
            
            <h3>💰 Comprar {selectedPackage.coins} monedas</h3>
            <p className="price">${selectedPackage.price} USD</p>
            
            <PayPalScriptProvider options={{ 
              "client-id": "ARKP0fmPopTezQ64buDp1QRus-7KkPiv9rYWzS6AjlSQo6RPIkHyzhsKMimHxw2KTziLonaw5i-b5yWO",
              currency: "USD"
            }}>
              <PayPalButtons
                createOrder={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      throw new Error('No hay sesión activa');
                    }

                    const response = await fetch(`${process.env.REACT_APP_API_URL}/coins/create-order`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({ packageId: selectedPackage.id })
                    });

                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Error al crear orden');
                    }

                    const data = await response.json();
                    
                    if (!data.orderId) {
                      throw new Error('No se recibió el ID de orden');
                    }

                    console.log('✅ Orden creada:', data.orderId);
                    return data.orderId;
                  } catch (error) {
                    console.error('❌ Error al crear orden:', error);
                    alert('Error: ' + error.message);
                    throw error;
                  }
                }}
                onApprove={async (data) => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/coins/capture-order`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({ orderId: data.orderID })
                    });

                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Error al procesar pago');
                    }

                    const result = await response.json();
                    
                    if (result.success) {
                      alert(`¡${result.coinsAdded} 💰 monedas acreditadas!`);
                      if (onPurchaseSuccess) {
                        onPurchaseSuccess(result.coinsAdded);
                      }
                      setShowPayPal(false);
                      setSelectedPackage(null);
                    } else {
                      throw new Error('No se pudo completar la compra');
                    }
                  } catch (error) {
                    console.error('❌ Error al capturar pago:', error);
                    alert('Error: ' + error.message);
                  }
                }}
                onError={(err) => {
                  console.error('❌ Error de PayPal:', err);
                  alert('Error al procesar el pago. Por favor intenta de nuevo.');
                }}
                onCancel={() => {
                  console.log('⚠️ Pago cancelado por el usuario');
                  setShowPayPal(false);
                  setSelectedPackage(null);
                }}
              />
            </PayPalScriptProvider>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoinShop;
